import React from 'react';
import './CreatePassword.scss';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { TwTitle, TwTitleColor, TwTitleType } from '../../components/TwTitles/TwTitles';
import { ReactComponent as TruckbaseLogo } from '../../assets/truckbase-logo.svg';
import { ReactComponent as TbLogo } from '../../assets/tb-logo.svg';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import { Formik, Form, FormikHelpers } from 'formik';
import AuthService from '../../providers/Auth/Auth.service';
import { useNavigate, useParams } from 'react-router-dom';
import { CreatePasswordData } from '../../providers/Auth/Auth.model';
import TwAlert from '../../components/TwAlerts/TwAlerts';

interface CreatePasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const CreatePassword: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { token } = useParams();

  const initialValues: CreatePasswordFormValues = {
    newPassword: '',
    confirmPassword: ''
  };

  const createPasswordSubmit = (
    values: CreatePasswordFormValues,
    token: string | undefined,
    actions: FormikHelpers<CreatePasswordFormValues>
  ): void => {
    const passwordData: CreatePasswordData = {
      password: values.newPassword,
      confirmPassword: values.confirmPassword,
      token: token
    };

    AuthService.createPassword(passwordData)
      .then(() => {
        navigate('/create-password/success');
      })
      .catch((error) => actions.setStatus(error.message));
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={(
          values: CreatePasswordFormValues,
          actions: FormikHelpers<CreatePasswordFormValues>
        ): void => {
          createPasswordSubmit(values, token, actions);
        }}
      >
        {({ values, handleChange, handleSubmit, status }): JSX.Element => (
          <Form>
            <div className="create-password__wrapper tid-create-password__wrapper">
              <div className="create-password__form-section">
                <div className="create-password__form-logo">
                  <TbLogo />
                </div>

                <div className="create-password__title">
                  <TwTitle type={TwTitleType.secondary} text="Create password" />
                </div>

                <div className="create-password__inputs-container">
                  <span className="create-password__instructions">
                    Your new password must be different from your previous password.
                  </span>

                  {status && <TwAlert message={status} />}

                  <div className="tid-create-password__password">
                    <TwField
                      id="newPassword"
                      name="newPassword"
                      value={values.newPassword}
                      label="Password"
                      inputType={TwInputTypes.password}
                      disabled={false}
                      onInputChange={handleChange}
                      error={false}
                    />
                  </div>

                  <div className="tid-create-password__confirm-password">
                    <TwField
                      id="confirmPassword"
                      name="confirmPassword"
                      value={values.confirmPassword}
                      label="Confirm password"
                      inputType={TwInputTypes.password}
                      disabled={false}
                      onInputChange={handleChange}
                      error={false}
                    />
                  </div>

                  <div className="create-password__action-container">
                    <div className="create-password__submit-button">
                      <TwButton
                        label="Reset password"
                        disabled={false}
                        fullwidth
                        type={TwButtonType.primary}
                        action={(): void => {
                          if (values.newPassword && values.confirmPassword) {
                            handleSubmit();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="create-password__photo-section">
                <div className="create-password__logo-container">
                  <TruckbaseLogo className="create-password__logo" />
                </div>
                <TwTitle type={TwTitleType.primary} text="More trucking." />
                <TwTitle
                  type={TwTitleType.primary}
                  color={TwTitleColor.red}
                  text="Less paperwork."
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreatePassword;
